import { SelectionInputLayout, Toggle, Tooltip, notification, toast } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useUpdateEmailVerificationMutation } from 'src/generated/graphql-manager';

type Props = {
  isEmailWhitelistPolicyEnabled: boolean;
  isEmailRegistrationPolicyEnabled: boolean;
  requireEmailWhitelistEmailVerification: boolean;
  requireEmailRegistrationEmailVerification: boolean;
  allowDisablingOnly: boolean;
  formDisabled?: boolean;
  refetch: () => void;
};

export function EmailVerification({
  isEmailWhitelistPolicyEnabled,
  isEmailRegistrationPolicyEnabled,
  requireEmailWhitelistEmailVerification,
  requireEmailRegistrationEmailVerification,
  allowDisablingOnly,
  formDisabled,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();

  const emailVerificationRequired = requireEmailRegistrationEmailVerification || requireEmailWhitelistEmailVerification;

  const emailVerificationEnabled = isEmailRegistrationPolicyEnabled || isEmailWhitelistPolicyEnabled;

  const [updateEmailVerificationMutation] = useUpdateEmailVerificationMutation();

  const updateEmailVerification = useCallback(() => {
    updateEmailVerificationMutation({
      variables: {
        input: {
          webcastId,
          requireEmailVerification: !emailVerificationRequired,
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data.updateEmailVerification.__typename === 'UpdateEmailVerificationSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  }, [emailVerificationRequired, refetch, t, updateEmailVerificationMutation, webcastId]);

  useEffect(() => {
    if (!isEmailWhitelistPolicyEnabled && !isEmailRegistrationPolicyEnabled && emailVerificationRequired)
      updateEmailVerification();
  }, [
    emailVerificationRequired,
    isEmailRegistrationPolicyEnabled,
    isEmailWhitelistPolicyEnabled,
    updateEmailVerification,
  ]);

  return (
    <Tooltip
      label={t(
        'manager.webcastSetup.eventAccess.registration.requireEmailVerification.disabledEmailVerificationTooltip'
      )}
      hidden={isEmailRegistrationPolicyEnabled || isEmailWhitelistPolicyEnabled}
    >
      <SelectionInputLayout
        variant="ghost"
        checked={emailVerificationRequired}
        disabled={!emailVerificationEnabled || (allowDisablingOnly && !emailVerificationRequired) || formDisabled}
        inputElement={
          <Toggle
            data-testid="email-verification-toggle"
            checked={emailVerificationRequired}
            disabled={!emailVerificationEnabled || (allowDisablingOnly && !emailVerificationRequired) || formDisabled}
            onChange={updateEmailVerification}
          >
            {t('manager.webcastSetup.eventAccess.registration.requireEmailVerification.label')}
          </Toggle>
        }
      />
    </Tooltip>
  );
}
